import {
  getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions,
  useAdminOrganizationGetCurrentAndActiveSemester,
} from "@/orval/admin-organization";
import { createFileRoute } from "@tanstack/react-router";
import { adminFeeCollectionGetFeeCollections } from "@/orval/admin-fee-collection";
import { DataTable } from "@/components/data-table/DataTable";
import { Form } from "@/components/form/form";
import { OrganizationSemestersComboboxFormField } from "@/components/form/form-field/combobox/organization-semesters-combobox-form-field";
import type { AdminOrganizationFeeCollectionDTO } from "@/orval/model";
import type { ColumnDef } from "@tanstack/react-table";
import { formatCurrency } from "@/lib/currency-utils";
import { PortalPageHeader } from "@/components/portal-page-header";
import { optionalSemesterSearchSchema } from "@/lib/route-search";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/fee-collections")({
  component: RouteComponent,
  validateSearch: optionalSemesterSearchSchema,
  loader: async ({ params, location, ...loaderContext }) => {
    const search = optionalSemesterSearchSchema.parse(location.search);
    let semesterId = search.semesterId;
    if (semesterId === undefined) {
      const semesters = await loaderContext.context.queryClient.ensureQueryData(
        getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions(params.organizationId),
      );
      semesterId = semesters?.nextSemesterId ?? semesters?.activeSemesterId ?? undefined;
    }

    if (!semesterId) {
      return;
    }

    const queryOptions = buildDataTableQueryOptions({});
    return ensureRouteQueriesData(loaderContext, [
      {
        queryKey: ["fee-collections", params.organizationId, semesterId, queryOptions, []],
        queryFn: () => adminFeeCollectionGetFeeCollections(params.organizationId, semesterId, [], queryOptions),
      },
    ]);
  },
});

const columns: ColumnDef<AdminOrganizationFeeCollectionDTO>[] = [
  {
    header: "Fee Name",
    accessorKey: "feeName",
  },
  {
    header: "Class Days",
    accessorFn: (row) => row.applicableClassDays.join(", "),
  },
  {
    header: "Amount Collected",
    accessorFn: (row) => formatCurrency(row.amountCollected),
  },
];

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: currentAndNextSemester } = useAdminOrganizationGetCurrentAndActiveSemester(organizationId);

  const semesterId =
    (search.semesterId ?? currentAndNextSemester?.nextSemesterId ?? currentAndNextSemester?.activeSemesterId) ||
    undefined;

  const defaultFormValues = {
    ...search,
    semesterId,
  };

  const getFeeCollections = adminFeeCollectionGetFeeCollections;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader className="mb-8" title="Fees Collected" />

      <DataTable
        columns={columns}
        renderFilters={
          <Form<{ semesterId?: number }>
            useFormProps={{ defaultValues: defaultFormValues }}
            onChange={(data) => navigate({ to: ".", search: data })}
            containerClassName="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:gap-4 md:space-y-0"
          >
            <OrganizationSemestersComboboxFormField
              className="w-full md:w-80"
              name="semesterId"
              organizationId={organizationId}
            />
          </Form>
        }
        missingRequiredFilterValues={!semesterId}
        missingRequiredFilterValuesMessage="Please select a semester."
        queryFn={(queryOptions, signal, filters) =>
          getFeeCollections(organizationId, semesterId!, filters ?? [], queryOptions, signal)
        }
        queryKeyFn={(params, filters) => ["fee-collections", organizationId, semesterId!, params, filters]}
        getRowIdFn={(row) => row.feeStructureId.toString()}
      />
    </div>
  );
}
